/**
 * QuestBoard.jsx — user-facing /quests page.
 *
 * Iter 64 — strict compliance + admin approval workflow.
 *
 * Manual quests with `params.requires_text_proof` and/or
 * `params.requires_approval` use a Submit panel (textarea + word
 * counter) instead of the old single-tap "Mark progress" button. The
 * server validates min/max word count and only flips the quest to
 * "claimable" once an admin signs off (or auto-approves when the quest
 * doesn't require approval).
 *
 * States rendered per card:
 *   • Open       — Mark progress / Open submission panel
 *   • Submitted  — "Awaiting admin approval" pill (broadcast)
 *   • Rejected   — Resubmit allowed, review notes shown
 *   • Approved   — Claim button unlocked
 *   • Claimed    — final "Claimed" badge
 */
import React, { useCallback, useEffect, useState, useMemo } from 'react';
import {
  Trophy, Loader2, CheckCircle2, Coins, Gem, Sparkles, Crown,
  Lock, Calendar, ClipboardEdit, FileText, Hourglass, XCircle, RefreshCw, AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import { authFetch, formatApiError } from '../utils/authUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const countWords = (s) => (s ? s.trim().split(/\s+/).filter(Boolean).length : 0);

const RewardChips = ({ rewards }) => {
  const items = [];
  if (rewards?.coins) items.push({ icon: Coins, label: `${rewards.coins} coins`, tint: 'bg-amber-500/20 border-amber-400/40 text-amber-200' });
  if (rewards?.gems) items.push({ icon: Gem, label: `${rewards.gems} gems`, tint: 'bg-fuchsia-500/20 border-fuchsia-400/40 text-fuchsia-200' });
  if (rewards?.xp) items.push({ icon: Sparkles, label: `${rewards.xp} XP`, tint: 'bg-indigo-500/20 border-indigo-400/40 text-indigo-200' });
  if (rewards?.premium_days) items.push({ icon: Crown, label: `${rewards.premium_days}d premium`, tint: 'bg-yellow-500/20 border-yellow-400/40 text-yellow-200' });
  if (rewards?.achievement_id) items.push({ icon: Trophy, label: 'Achievement', tint: 'bg-emerald-500/20 border-emerald-400/40 text-emerald-200' });
  if (rewards?.custom_text) items.push({ icon: Sparkles, label: rewards.custom_text, tint: 'bg-pink-500/20 border-pink-400/40 text-pink-200' });
  if (!items.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5 mt-2">
      {items.map((r, i) => (
        <span key={i} className={`inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full border ${r.tint}`}>
          <r.icon className="w-3 h-3" /> {r.label}
        </span>
      ))}
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────────────
// Submit panel — appears for quests with text-proof or approval params.
// Server is the source of truth for word count; the panel just gives the
// user immediate feedback so they know they're meeting the floor.
// ────────────────────────────────────────────────────────────────────────────
const SubmitPanel = ({ quest, onSubmitted }) => {
  const params = quest.params || {};
  const minWords = Number(params.min_word_count || 0);
  const maxWords = Number(params.max_word_count || 0);
  const requiresProof = !!params.requires_text_proof;
  const requiresApproval = !!params.requires_approval;
  const [text, setText] = useState('');
  const [link, setLink] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const wc = useMemo(() => countWords(text), [text]);
  const meetsMin = !minWords || wc >= minWords;
  const meetsMax = !maxWords || wc <= maxWords;
  const canSubmit = !busy && (!requiresProof || (text.trim() && meetsMin && meetsMax));

  const submit = async () => {
    setBusy(true);
    setErr('');
    try {
      const r = await authFetch(`${BACKEND_URL}/api/quests/${quest.quest_id}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, link, notes }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(formatApiError(d.detail));
      if (d.approval_status === 'pending') {
        toast.success('Submitted! Awaiting admin approval.');
      } else {
        toast.success('Submitted — you can claim your rewards now.');
      }
      setText('');
      setLink('');
      setNotes('');
      onSubmitted?.();
    } catch (e) {
      setErr(typeof e?.message === 'string' ? e.message : 'Submission failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="mt-3 rounded-lg border border-indigo-400/30 bg-indigo-500/5 p-3 space-y-2"
      data-testid={`quest-submit-panel-${quest.quest_id}`}
    >
      <div className="flex items-center justify-between text-[11px] uppercase tracking-widest font-bold text-indigo-300">
        <span className="flex items-center gap-1">
          <ClipboardEdit className="w-3 h-3" /> Submit your work
        </span>
        {requiresApproval && (
          <span className="text-amber-300 text-[10px]">Admin approval required</span>
        )}
      </div>
      {requiresProof && (
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={
            minWords
              ? `Paste your text here — minimum ${minWords} words${maxWords ? ` (cap ${maxWords})` : ''}.`
              : 'Paste your text here…'
          }
          rows={6}
          className="w-full bg-slate-900/80 border border-slate-700 rounded-md p-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-indigo-400 resize-y"
          data-testid={`quest-submit-text-${quest.quest_id}`}
        />
      )}
      {requiresProof && (
        <div className="flex items-center justify-between text-[11px]">
          <span className={`tabular-nums font-bold ${
            meetsMin && meetsMax ? 'text-emerald-300' : 'text-rose-300'
          }`} data-testid={`quest-wordcount-${quest.quest_id}`}>
            {wc} words
            {minWords ? ` · min ${minWords}` : ''}
            {maxWords ? ` · max ${maxWords}` : ''}
          </span>
          {minWords > 0 && (
            <span className="text-slate-400">
              {meetsMin ? 'Meets minimum ✓' : `${minWords - wc} more to go`}
            </span>
          )}
        </div>
      )}
      <input
        type="url"
        value={link}
        onChange={(e) => setLink(e.target.value)}
        placeholder="Optional link (Google Doc, GitHub, etc.)"
        className="w-full bg-slate-900/80 border border-slate-700 rounded-md px-2 py-1.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-indigo-400"
        data-testid={`quest-submit-link-${quest.quest_id}`}
      />
      <input
        type="text"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Notes for the reviewer (optional)"
        className="w-full bg-slate-900/80 border border-slate-700 rounded-md px-2 py-1.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-indigo-400"
        data-testid={`quest-submit-notes-${quest.quest_id}`}
      />
      {err && (
        <p className="text-xs text-rose-300" data-testid={`quest-submit-error-${quest.quest_id}`}>{err}</p>
      )}
      <button
        type="button"
        onClick={submit}
        disabled={!canSubmit}
        className={`w-full px-3 py-2 rounded-lg text-sm font-bold text-white flex items-center justify-center gap-1 transition-colors ${
          canSubmit
            ? 'bg-indigo-600 hover:bg-indigo-500'
            : 'bg-slate-700 cursor-not-allowed opacity-60'
        }`}
        data-testid={`quest-submit-btn-${quest.quest_id}`}
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
        {busy ? 'Submitting…' : 'Submit for review'}
      </button>
    </div>
  );
};

const QuestCard = ({ quest, onChange }) => {
  const [busy, setBusy] = useState(false);
  const [showPanel, setShowPanel] = useState(false);
  const target = Math.max(quest.target || 1, 1);
  const progress = quest.user_progress?.progress || 0;
  const pct = Math.min(100, Math.round((progress / target) * 100));
  const completed = quest.user_progress?.completed;
  const claimed = quest.user_progress?.claimed;
  const approvalStatus = quest.user_progress?.approval_status; // pending|approved|rejected|null
  const params = quest.params || {};
  const needsSubmission = !!(params.requires_text_proof || params.requires_approval);

  const bump = async () => {
    setBusy(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/quests/${quest.quest_id}/progress`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}',
      });
      const d = await r.json();
      if (!r.ok) throw new Error(formatApiError(d.detail));
      toast.success(`Progress: ${d.progress}/${d.target}`);
      onChange?.();
    } catch (err) { toast.error(typeof err?.message === 'string' ? err.message : 'Error'); }
    finally { setBusy(false); }
  };

  const claim = async () => {
    setBusy(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/quests/${quest.quest_id}/claim`, { method: 'POST' });
      const d = await r.json();
      if (!r.ok) throw new Error(formatApiError(d.detail));
      const bits = [];
      if (d.rewards.coins) bits.push(`${d.rewards.coins} coins`);
      if (d.rewards.gems) bits.push(`${d.rewards.gems} gems`);
      if (d.rewards.xp) bits.push(`${d.rewards.xp} XP`);
      if (d.rewards.achievement_id) bits.push('an achievement');
      if (d.rewards.premium_days) bits.push(`${d.rewards.premium_days}d premium`);
      toast.success(`Claimed${bits.length ? `: ${bits.join(', ')}` : ''}!`);
      onChange?.();
    } catch (err) { toast.error(typeof err?.message === 'string' ? err.message : 'Error'); }
    finally { setBusy(false); }
  };

  // Choose the border accent based on the dominant state.
  const cardBorder =
    approvalStatus === 'pending' ? 'border-amber-400/50 bg-amber-500/5' :
    approvalStatus === 'rejected' ? 'border-rose-400/50 bg-rose-500/5' :
    (completed && approvalStatus !== 'rejected') ? 'border-emerald-400/40 bg-emerald-500/5' :
    'border-slate-700 bg-slate-800/50';

  return (
    <article
      className={`rounded-xl p-4 border ${cardBorder}`}
      data-testid={`quest-card-${quest.quest_id}`}
    >
      <div className="flex items-start gap-2 mb-1">
        {quest.type === 'secret' && <Lock className="w-4 h-4 text-rose-400 flex-shrink-0 mt-0.5" />}
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-bold text-white">{quest.title}</h3>
          {quest.description && <p className="text-xs text-slate-300 mt-0.5 whitespace-pre-wrap">{quest.description}</p>}
          {(params.min_word_count || params.requires_approval) && (
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {params.min_word_count && (
                <span className="text-[10px] inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-indigo-500/20 border border-indigo-400/30 text-indigo-200 font-bold uppercase tracking-wider">
                  Min {params.min_word_count} words
                </span>
              )}
              {params.max_word_count ? (
                <span className="text-[10px] inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-indigo-500/20 border border-indigo-400/30 text-indigo-200 font-bold uppercase tracking-wider">
                  Cap {params.max_word_count} words
                </span>
              ) : null}
              {params.requires_approval && (
                <span className="text-[10px] inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-amber-500/20 border border-amber-400/30 text-amber-200 font-bold uppercase tracking-wider">
                  Admin reviewed
                </span>
              )}
            </div>
          )}
        </div>
        {(quest.start_at || quest.end_at) && (
          <span className="text-[10px] inline-flex items-center gap-1 text-slate-400 flex-shrink-0">
            <Calendar className="w-3 h-3" />
            {quest.start_at ? formatDate(quest.start_at) : ''}
            {quest.start_at && quest.end_at ? ' – ' : ''}
            {quest.end_at ? formatDate(quest.end_at) : ''}
          </span>
        )}
      </div>

      <RewardChips rewards={quest.rewards} />

      <div className="mt-3">
        <div className="flex items-center justify-between text-[11px] text-slate-400 mb-1">
          <span className="uppercase tracking-wider font-bold">Progress</span>
          <span className="tabular-nums">{progress} / {target}</span>
        </div>
        <div
          className="h-2 rounded-full bg-slate-700/60 overflow-hidden"
          role="progressbar"
          aria-label={`${quest.title} progress`}
          aria-valuemin={0}
          aria-valuemax={target}
          aria-valuenow={Math.min(progress, target)}
          aria-valuetext={`${progress} of ${target}`}
        >
          <div
            className={`h-full transition-all duration-500 ${
              approvalStatus === 'pending' ? 'bg-amber-400' :
              approvalStatus === 'rejected' ? 'bg-rose-400' :
              completed ? 'bg-emerald-400' : 'bg-indigo-500'
            }`}
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      {/* Status banner (broadcast) — replaces the action buttons when the
          quest is in a transitional state. */}
      {approvalStatus === 'pending' && (
        <div
          className="mt-3 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-amber-500/15 border border-amber-400/40 text-amber-200 text-xs font-bold w-full justify-center"
          data-testid={`quest-pending-${quest.quest_id}`}
        >
          <Hourglass className="w-3.5 h-3.5" />
          Completed · awaiting admin approval
        </div>
      )}
      {approvalStatus === 'rejected' && (
        <div
          className="mt-3 px-3 py-2 rounded-lg bg-rose-500/15 border border-rose-400/40 text-rose-200 text-xs"
          data-testid={`quest-rejected-${quest.quest_id}`}
        >
          <div className="flex items-center gap-1.5 font-bold">
            <XCircle className="w-3.5 h-3.5" /> Submission rejected
          </div>
          {quest.user_progress?.review_notes && (
            <p className="mt-1 text-[11px] text-rose-100/80 whitespace-pre-wrap">
              {quest.user_progress.review_notes}
            </p>
          )}
        </div>
      )}

      {/* Action row */}
      <div className="flex items-center gap-2 mt-3">
        {/* Not yet completed + manual without compliance → simple progress button */}
        {!completed && quest.tracking_mode === 'manual' && !needsSubmission && (
          <button
            type="button"
            onClick={bump}
            disabled={busy}
            className="flex-1 px-3 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-600 text-white text-sm font-semibold"
            data-testid={`quest-progress-${quest.quest_id}`}
          >
            {busy ? <Loader2 className="w-4 h-4 inline-block animate-spin mr-1" /> : null}
            Mark progress
          </button>
        )}
        {/* Compliance quest → open the Submit panel */}
        {!completed && needsSubmission && approvalStatus !== 'pending' && approvalStatus !== 'rejected' && (
          <button
            type="button"
            onClick={() => setShowPanel((v) => !v)}
            disabled={busy}
            className="flex-1 px-3 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-600 text-white text-sm font-semibold inline-flex items-center justify-center gap-1"
            data-testid={`quest-open-submit-${quest.quest_id}`}
          >
            <ClipboardEdit className="w-4 h-4" />
            {showPanel ? 'Hide submission' : 'Submit work'}
          </button>
        )}
        {/* Rejected + compliance → resubmit shortcut */}
        {approvalStatus === 'rejected' && (
          <button
            type="button"
            onClick={() => setShowPanel(true)}
            className="flex-1 px-3 py-2 rounded-lg bg-rose-600 hover:bg-rose-500 text-white text-sm font-semibold inline-flex items-center justify-center gap-1"
            data-testid={`quest-resubmit-${quest.quest_id}`}
          >
            <ClipboardEdit className="w-4 h-4" /> Resubmit
          </button>
        )}
        {/* Approved (or non-compliance completed) → Claim */}
        {completed && !claimed && approvalStatus !== 'pending' && approvalStatus !== 'rejected' && (
          <button
            type="button"
            onClick={claim}
            disabled={busy}
            className="flex-1 px-3 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-600 text-white text-sm font-semibold flex items-center justify-center gap-1"
            data-testid={`quest-claim-${quest.quest_id}`}
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            Claim rewards
          </button>
        )}
        {claimed && (
          <span
            className="flex-1 px-3 py-2 rounded-lg bg-emerald-500/20 border border-emerald-400/40 text-emerald-200 text-sm font-bold text-center inline-flex items-center justify-center gap-1"
            data-testid={`quest-claimed-${quest.quest_id}`}
          >
            <CheckCircle2 className="w-4 h-4" /> Claimed
          </span>
        )}
      </div>

      {/* Submission panel — slides in when the user opens it */}
      {showPanel && (approvalStatus !== 'pending') && needsSubmission && !claimed && (
        <SubmitPanel
          quest={quest}
          onSubmitted={() => {
            setShowPanel(false);
            onChange?.();
          }}
        />
      )}
    </article>
  );
};

const QuestBoard = () => {
  const [quests, setQuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchQuests = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r = await authFetch(`${BACKEND_URL}/api/quests`);
      const d = await r.json();
      if (!r.ok) throw new Error(formatApiError(d.detail));
      setQuests(Array.isArray(d.quests) ? d.quests : []);
    } catch (err) {
      setError(typeof err?.message === 'string' ? err.message : 'We could not load your quests. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchQuests(); }, [fetchQuests]);

  // Group quests by status for the top counter row.
  const stats = useMemo(() => {
    const s = { open: 0, pending: 0, approved: 0, rejected: 0, claimed: 0 };
    for (const q of quests) {
      const up = q.user_progress || {};
      if (up.claimed) s.claimed++;
      else if (up.approval_status === 'pending') s.pending++;
      else if (up.approval_status === 'rejected') s.rejected++;
      else if (up.completed) s.approved++;
      else s.open++;
    }
    return s;
  }, [quests]);

  return (
    <main className="sharp-bg min-h-screen" data-testid="quest-board-page">
      <div className="max-w-3xl mx-auto px-4 py-6 sm:py-8">
        <section className="relative overflow-hidden rounded-2xl border border-violet-400/20 bg-gradient-to-br from-indigo-950 via-[#12131A] to-amber-950/30 p-5 sm:p-7 mb-5">
          <div className="pointer-events-none absolute -right-10 -top-14 h-40 w-40 rounded-full bg-amber-400/10 blur-3xl" aria-hidden="true" />
          <div className="relative flex items-start justify-between gap-4">
            <div>
              <p className="eyebrow text-amber-200 mb-2">Your rewards journey</p>
              <h1 className="font-display text-3xl sm:text-4xl font-black text-white flex items-center gap-2">
                <Trophy className="w-7 h-7 text-amber-300" aria-hidden="true" /> Quest Board
              </h1>
              <p className="text-sm text-slate-300 mt-2 max-w-xl">Your available quests, review status, and server-confirmed rewards in one place.</p>
            </div>
            <button
              type="button"
              onClick={fetchQuests}
              disabled={loading}
              className="shrink-0 inline-flex items-center gap-1.5 rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-xs font-bold text-slate-200 hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-60"
              aria-label="Refresh quests"
              data-testid="quest-board-refresh"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} aria-hidden="true" /> Refresh
            </button>
          </div>
        </section>

        {!loading && !error && quests.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-5" data-testid="quest-board-stats" aria-label="Quest status summary">
            {stats.open > 0 && (
              <span className="text-[11px] inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-indigo-500/15 border border-indigo-400/30 text-indigo-100 font-bold">
                <ClipboardEdit className="w-3 h-3" aria-hidden="true" /> {stats.open} in progress
              </span>
            )}
            {stats.pending > 0 && (
              <span className="text-[11px] inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-amber-500/20 border border-amber-400/40 text-amber-100 font-bold">
                <Hourglass className="w-3 h-3" aria-hidden="true" /> {stats.pending} awaiting review
              </span>
            )}
            {stats.rejected > 0 && (
              <span className="text-[11px] inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-rose-500/20 border border-rose-400/40 text-rose-100 font-bold">
                <XCircle className="w-3 h-3" aria-hidden="true" /> {stats.rejected} need rework
              </span>
            )}
            {stats.approved > 0 && (
              <span className="text-[11px] inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-emerald-500/20 border border-emerald-400/40 text-emerald-100 font-bold">
                <CheckCircle2 className="w-3 h-3" aria-hidden="true" /> {stats.approved} ready to claim
              </span>
            )}
            {stats.claimed > 0 && (
              <span className="text-[11px] inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-slate-700/50 border border-slate-600 text-slate-200 font-bold">
                <Sparkles className="w-3 h-3" aria-hidden="true" /> {stats.claimed} claimed
              </span>
            )}
          </div>
        )}

        {loading && (
          <div className="sharp-card flex items-center justify-center gap-2 text-sm text-slate-300 py-10" role="status">
            <Loader2 className="w-5 h-5 animate-spin text-amber-300" aria-hidden="true" /> Loading your quests…
          </div>
        )}
        {!loading && error && (
          <div className="rounded-xl border border-rose-400/40 bg-rose-500/10 p-4 text-sm text-rose-100" role="alert" data-testid="quest-board-error">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-rose-300" aria-hidden="true" />
              <div className="min-w-0 flex-1">
                <p className="font-bold">Quests are unavailable right now.</p>
                <p className="mt-1 text-rose-100/80">{error}</p>
              </div>
              <button type="button" onClick={fetchQuests} className="shrink-0 rounded-md border border-rose-300/50 px-2.5 py-1.5 text-xs font-bold hover:bg-rose-400/10" data-testid="quest-board-retry">Try again</button>
            </div>
          </div>
        )}
        {!loading && !error && quests.length === 0 && (
          <div className="sharp-card p-8 text-center" data-testid="quest-board-empty">
            <Trophy className="w-8 h-8 text-amber-300/70 mx-auto mb-3" aria-hidden="true" />
            <p className="font-display text-xl font-bold text-white">No quests available</p>
            <p className="mt-1 text-sm text-slate-400">Check back when new quests are published for you.</p>
          </div>
        )}
        {!loading && !error && quests.length > 0 && (
          <div className="space-y-3" data-testid="quest-board-list">
            {quests.map((q) => (
              <QuestCard key={q.quest_id} quest={q} onChange={fetchQuests} />
            ))}
          </div>
        )}
      </div>
    </main>
  );
};

export default QuestBoard;
